import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '../../layout/memberPrimitives';
import type { BookingStatus } from '../../../utils/dashboard/types';

export interface IntakeProgress {
  ready: boolean;
  completionRatio: number;
  requiredPassed: number;
  requiredTotal: number;
  bookingStatus: BookingStatus;
  /** Readiness blocker ids still outstanding, so the prompt can name them. */
  outstandingIds?: string[];
}

/**
 * Member-facing phrasing for each readiness blocker.
 *
 * The readiness checks are written for clinicians — "Mother captured (alive,
 * deceased, or explicitly unknown)" — and were never shown to members at all.
 * A bare percentage was, which is why "60%" after filling in two hundred fields
 * read as broken: the number counts five clinical prerequisites and nothing
 * else, so most of the form cannot move it.
 *
 * Naming what is actually left is both truer and more useful than any
 * percentage. The number stays, because it is the same one the submit gate
 * uses and a second, kinder number that disagreed with it would be worse.
 */
const MEMBER_BLOCKER_COPY: Record<string, string> = {
  'personal.dob': 'your date of birth',
  'personal.sex': 'your sex at birth',
  'family.mother': 'your mother',
  'family.father': 'your father',
  'family.conditions_coded': 'a condition that needs picking from the list',
};

function outstandingSentence(ids: string[] | undefined): string | null {
  if (!ids || ids.length === 0) return null;
  const parts = ids.map((id) => MEMBER_BLOCKER_COPY[id]).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) return `All that is left is ${parts[0]}.`;
  const last = parts[parts.length - 1];
  return `What is left: ${parts.slice(0, -1).join(', ')} and ${last}.`;
}

/**
 * Session key, not a persisted preference.
 *
 * The dialog returns on the member's next visit, because the thing it is asking
 * for is still outstanding — a prompt that can be dismissed forever is a prompt
 * for something optional, and the history is not optional; it is what the
 * quarterly report is written from. Once per session is the compromise: never
 * twice in a sitting, never gone for good. The banner below it does not
 * dismiss at all, so the answer to "where was that thing?" is always on screen.
 */
const DISMISS_KEY = 'melio.intakePrompt.dismissed.v1';

const OXBLOOD = '#5C1A1C';

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    // Private browsing and blocked storage both throw here. Treat it as
    // "already prompted" rather than prompting on every single render.
    return true;
  }
}

function writeDismissed() {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* nothing to do — the prompt simply returns on the next render path */
  }
}

/**
 * What the member is being asked for, and where it lives.
 *
 * TWO STAGES, IN THIS ORDER. The history at /history comes first; the clinical
 * history interview is booked after it.
 *
 * The first cut had this the wrong way round — an unbooked member was sent
 * straight to /book/clinical-history and told there was nothing to fill in
 * beforehand, which is how someone ends up holding an appointment with an empty
 * record behind it. The history is the thing the care team reads first and the
 * thing the interview works from, so it is what the prompt asks for until it is
 * done.
 *
 * Returns null when nothing is outstanding: history complete AND the interview
 * booked. The component renders nothing in that case.
 */
function copyFor(progress: IntakeProgress) {
  const percent = Math.round(progress.completionRatio * 100);

  // Stage 1 — the history itself.
  if (!progress.ready) {
    const outstanding = outstandingSentence(progress.outstandingIds);
    return {
      percent,
      showProgress: percent > 0,
      eyebrow: percent === 0 ? 'Your first step' : 'Your health history',
      title: percent === 0 ? 'Start your health history.' : 'Your health history is nearly there.',
      body:
        percent === 0
          ? 'This is the first thing we ask for — the record your care team reads before anything else, and what your clinical history interview works from.'
          : outstanding
            ? `${outstanding} Your care team reads this before every review, and the gaps are the parts they cannot account for.`
            : 'Your care team reads this before every review — the gaps are the parts they cannot account for.',
      ctaLabel: percent === 0 ? 'Start your history' : 'Continue your history',
      href: '/history',
    };
  }

  // Stage 2 — the interview, now that there is a history to discuss.
  if (progress.bookingStatus === 'none') {
    return {
      percent,
      showProgress: false,
      eyebrow: 'Next step',
      title: 'Book your clinical history interview.',
      body:
        'Your history is complete. A clinician will go through it with you on a video call and fill in anything it raises.',
      ctaLabel: 'Book your interview',
      href: '/book/clinical-history',
    };
  }

  return null;
}

function ProgressBar({ percent }: { percent: number }) {
  return (
    <div
      className="h-1 rounded-full overflow-hidden"
      style={{ backgroundColor: 'var(--melio-border)' }}
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Health history completeness"
    >
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${Math.max(2, percent)}%`, backgroundColor: OXBLOOD }}
      />
    </div>
  );
}

/**
 * The pop-up. Shown once per session while the history is unfinished.
 *
 * Deliberately NOT blocking, unlike FirstLoginConsentGate: consent is a legal
 * precondition to showing a member their record, whereas an unfinished history
 * is a nudge. Escape, the backdrop and "Not now" all close it, and the banner
 * behind it carries the same call to action for anyone who does.
 */
function IntakeDialog({
  copy,
  onClose,
}: {
  copy: NonNullable<ReturnType<typeof copyFor>>;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    primaryRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const go = () => {
    onClose();
    navigate(copy.href);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: 'rgba(20, 18, 16, 0.55)' }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="intake-prompt-heading"
        className="w-full rounded-xl p-7 md:p-8"
        style={{
          maxWidth: 520,
          backgroundColor: 'white',
          border: '1px solid var(--melio-border)',
          boxShadow: '0 24px 60px rgba(20, 18, 16, 0.28)',
        }}
        // The backdrop closes; the panel must not, or every click inside the
        // dialog dismisses it.
        onClick={(event) => event.stopPropagation()}
      >
        <p className="m-eyebrow mb-2" style={{ color: OXBLOOD }}>
          {copy.eyebrow}
        </p>
        <h2 id="intake-prompt-heading" className="m-title mb-3">
          {copy.title}
        </h2>
        <p className="text-sm mb-5" style={{ color: 'var(--melio-text-muted)', lineHeight: 1.65 }}>
          {copy.body}
        </p>

        {copy.showProgress && (
          <div className="mb-6">
            <ProgressBar percent={copy.percent} />
            <p className="mt-2 text-xs" style={{ color: 'var(--melio-text-muted)' }}>
              {copy.percent}% complete
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button
            ref={primaryRef}
            type="button"
            onClick={go}
            className="px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90"
            style={{ backgroundColor: OXBLOOD, color: 'var(--melio-cream)' }}
          >
            {copy.ctaLabel}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-3 rounded text-sm font-medium border transition-opacity hover:opacity-90"
            style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-ink)' }}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Home's standing prompt to get the baseline in place: the health history
 * first, then the interview that discusses it.
 *
 * Renders nothing at all once both are done, so a member who has finished
 * never sees it again.
 */
export default function IntakePrompt({ progress }: { progress: IntakeProgress | null }) {
  const copy = progress ? copyFor(progress) : null;
  const outstanding = !!copy;
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!outstanding) return;
    if (readDismissed()) return;
    setDialogOpen(true);
  }, [outstanding]);

  const closeDialog = useCallback(() => {
    writeDismissed();
    setDialogOpen(false);
  }, []);

  if (!progress || !copy) return null;

  return (
    <>
      <section
        aria-labelledby="intake-banner-heading"
        className="rounded-xl p-5 md:p-6"
        style={{
          border: '1px solid var(--melio-border)',
          borderLeft: `3px solid ${OXBLOOD}`,
          backgroundColor: 'var(--melio-cream-warm)',
        }}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div style={{ maxWidth: '38rem' }}>
            <p className="m-eyebrow text-[10px] mb-1" style={{ color: OXBLOOD }}>
              {copy.eyebrow}
            </p>
            <h2 id="intake-banner-heading" className="m-title mb-2">
              {copy.title}
            </h2>
            <p className="text-sm" style={{ color: 'var(--melio-text-muted)', lineHeight: 1.6 }}>
              {copy.body}
            </p>
          </div>

          <Link
            to={copy.href}
            className="inline-flex items-center gap-2 px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90 shrink-0"
            style={{ backgroundColor: OXBLOOD, color: 'var(--melio-cream)' }}
          >
            {copy.ctaLabel}
            <ChevronRightIcon size={16} />
          </Link>
        </div>

        {copy.showProgress && (
          <div className="mt-5">
            <ProgressBar percent={copy.percent} />
            <p className="mt-2 text-xs" style={{ color: 'var(--melio-text-muted)' }}>
              {copy.percent}% complete — {progress.requiredPassed} of {progress.requiredTotal} required
              sections done
            </p>
          </div>
        )}
      </section>

      {dialogOpen && <IntakeDialog copy={copy} onClose={closeDialog} />}
    </>
  );
}
